import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { InsightsDailyPoint } from '@/api/assistantInsights/assistantInsightsApi.types';
import { OptionChips } from '@/components/assistantConfiguration/OptionChips';
import { ChartCard } from '@/components/common/charts/ChartCard';
import {
  CHART_AXIS_TICK,
  CHART_TOOLTIP_CONTENT_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  buildLabelFormatter,
  buildValueFormatter,
} from '@/components/common/charts/chartStyles';
import { CHART_ROLE_COLORS, resolveSeriesColor } from '@/theme/chartPalette';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';
import { resolveTopicColor } from './topicPalette';
import { USAGE_TOPICS } from './usageFilters';
import { formatAxisDollars, formatCompactNumber, microUsdToDollars } from './usageFormatting';
import { useUsageLabels } from './useUsageLabels';

type TrendMetric = 'cost' | 'questions' | 'tokens';

const TREND_METRICS: TrendMetric[] = ['cost', 'questions', 'tokens'];
const QUESTIONS_SERIES_KEY = 'questions';
const TREND_CHART_HEIGHT = 280;

interface UsageTrendChartProps {
  points: InsightsDailyPoint[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelectDay: (isoDate: string) => void;
}

function buildRows(points: InsightsDailyPoint[], metric: TrendMetric) {
  return points.map(function toRow(point) {
    const row: Record<string, string | number> = { date: point.date };
    if (metric === 'questions') {
      row[QUESTIONS_SERIES_KEY] = point.questions;
      return row;
    }
    const values = metric === 'cost' ? point.costByTopic : point.tokensByTopic;
    USAGE_TOPICS.forEach(function addTopic(topic) {
      const value = values[topic] || 0;
      row[topic] = metric === 'cost' ? microUsdToDollars(value) : value;
    });
    return row;
  });
}

export function UsageTrendChart({
  points,
  isLoading,
  isError,
  onRetry,
  onSelectDay,
}: UsageTrendChartProps) {
  const { t, i18n } = useTranslation('assistant');
  const { topicLabel } = useUsageLabels();
  const [metric, setMetric] = useState<TrendMetric>('cost');
  const [hiddenTopics, setHiddenTopics] = useState<string[]>([]);
  const rows = buildRows(points, metric);
  const hasUsage = points.some(function hasActivity(point) {
    return point.questions > 0 || point.costMicroUsd > 0;
  });
  const formatValue =
    metric === 'cost'
      ? function formatDollars(value: number) {
          return formatAxisDollars(value, i18n.language);
        }
      : function formatCount(value: number) {
          return formatCompactNumber(value, i18n.language);
        };
  const visibleTopics = USAGE_TOPICS.filter(function isVisible(topic) {
    return hiddenTopics.includes(topic) === false;
  });

  function toggleTopic(topic: string) {
    setHiddenTopics(function flip(current) {
      return current.includes(topic)
        ? current.filter(function keep(candidate) {
            return candidate !== topic;
          })
        : [...current, topic];
    });
  }

  return (
    <ChartCard
      title={t('insights.trend.title')}
      iconName="BarChart"
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      isEmpty={hasUsage === false}
      emptyLabel={t('insights.trend.empty')}
      headerRight={
        <OptionChips
          label={t('insights.trend.metricLabel')}
          isLabelHidden
          options={TREND_METRICS.map(function toOption(option) {
            return { value: option, label: t('insights.trend.metric.' + option) };
          })}
          selectedValue={metric}
          onSelect={setMetric}
        />
      }
    >
      <div role="img" aria-label={t('insights.trend.title')} className="w-full">
        <ResponsiveContainer width="100%" height={TREND_CHART_HEIGHT}>
          <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={CHART_ROLE_COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={CHART_AXIS_TICK}
              tickFormatter={formatIsoDateToDayAndMonth}
              tickLine={false}
              axisLine={false}
              minTickGap={16}
            />
            <YAxis
              tick={CHART_AXIS_TICK}
              tickFormatter={formatValue}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            <Tooltip
              cursor={{ fill: CHART_ROLE_COLORS.grid, opacity: 0.4 }}
              contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
              labelStyle={CHART_TOOLTIP_LABEL_STYLE}
              itemStyle={CHART_TOOLTIP_ITEM_STYLE}
              formatter={buildValueFormatter(formatValue)}
              labelFormatter={buildLabelFormatter(formatIsoDateToDayAndMonth)}
            />
            {metric === 'questions' ? (
              <Bar
                dataKey={QUESTIONS_SERIES_KEY}
                name={t('insights.trend.metric.questions')}
                fill={resolveSeriesColor(0)}
                radius={[6, 6, 0, 0]}
                cursor="pointer"
                onClick={function zoomIntoDay(_entry, index) {
                  const point = points[index];
                  if (point !== undefined) {
                    onSelectDay(point.date);
                  }
                }}
              />
            ) : (
              visibleTopics.map(function renderTopic(topic, index) {
                return (
                  <Bar
                    key={topic}
                    dataKey={topic}
                    name={topicLabel(topic)}
                    stackId="topics"
                    fill={resolveTopicColor(topic)}
                    radius={index === visibleTopics.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                    cursor="pointer"
                    onClick={function zoomIntoDay(_entry, pointIndex) {
                      const point = points[pointIndex];
                      if (point !== undefined) {
                        onSelectDay(point.date);
                      }
                    }}
                  />
                );
              })
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {metric === 'questions' ? null : (
        <div className="flex flex-wrap gap-2 mt-3" aria-label={t('insights.trend.legend')}>
          {USAGE_TOPICS.map(function renderLegendItem(topic) {
            const isHidden = hiddenTopics.includes(topic);
            return (
              <button
                key={topic}
                type="button"
                aria-pressed={isHidden === false}
                onClick={function toggle() {
                  toggleTopic(topic);
                }}
                className={
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-opacity ' +
                  (isHidden ? 'opacity-40 border-zooborder' : 'border-transparent bg-zoooffwhite')
                }
              >
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: resolveTopicColor(topic) }}
                />
                {topicLabel(topic)}
              </button>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-zoomuted mt-2">{t('insights.trend.hint')}</p>
    </ChartCard>
  );
}
